import { Link } from "react-router-dom";
import type { Drama } from "@/mocks/dramas";

type Props = {
  drama: Drama;
  rank: number;
  metricLabel: string;
  metricValue: number;
};

export default function RankingRow({ drama, rank, metricLabel, metricValue }: Props) {
  const medal = rank <= 3;

  return (
    <article className="flex flex-col gap-4 rounded-lg border border-background-200/70 bg-background-100/60 p-4 transition-colors hover:border-primary-400/70 sm:flex-row sm:items-center">
      <div className="flex items-start gap-4 sm:items-center">
        <span
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md font-heading text-lg font-bold ${
            medal ? "bg-accent-500 text-background-950" : "bg-background-200 text-foreground-700"
          }`}
          aria-label={`第 ${rank} 名`}
        >
          {rank}
        </span>

        <Link
          to={`/drama/${drama.id}`}
          className="block w-16 shrink-0 overflow-hidden rounded-md cursor-pointer sm:w-20"
          aria-label={`查看 ${drama.title} 详情`}
        >
          <div className="relative w-full" style={{ aspectRatio: "3 / 4" }}>
            <img
              src={drama.cover}
              alt={`${drama.title} 短剧封面`}
              title={`${drama.title} 剧集封面`}
              className="absolute inset-0 h-full w-full object-cover object-top"
            />
          </div>
        </Link>
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="font-heading text-base font-bold text-foreground-950">
          <Link to={`/drama/${drama.id}`} className="cursor-pointer transition-colors hover:text-primary-400">
            {drama.title}
          </Link>
        </h3>
        <p className="mt-1 line-clamp-1 text-xs leading-relaxed text-foreground-600">{drama.subtitle}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-[11px] font-label text-accent-400">
            {drama.category}
          </span>
          {drama.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-full border border-background-300/60 px-2.5 py-0.5 text-[11px] font-label text-foreground-600"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 w-full max-w-[220px] overflow-hidden rounded-full bg-background-200">
            <div className="h-full rounded-full bg-primary-500" style={{ width: `${metricValue}%` }}></div>
          </div>
          <span className="text-[11px] font-label whitespace-nowrap text-foreground-500">
            {metricLabel} {metricValue}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 border-t border-background-200/70 pt-4 sm:flex-col sm:items-end sm:border-0 sm:pt-0">
        <div className="text-right">
          <div className="flex items-center gap-1 text-accent-400">
            <i className="ri-star-fill text-sm" aria-hidden="true"></i>
            <span className="font-heading text-lg font-bold">{drama.rating.toFixed(1)}</span>
          </div>
          <p className="text-[11px] font-label text-foreground-500">
            {drama.episodes} 集 · {drama.status}
          </p>
        </div>
        <Link
          to={`/drama/${drama.id}`}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300/70 px-3.5 py-2 text-xs font-label text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-400 cursor-pointer"
        >
          详情
          <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </Link>
      </div>
    </article>
  );
}